import { useState, useMemo } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogOut, Link as LinkIcon, Inbox, LayoutDashboard, Settings, Menu, X, BarChart3 } from 'lucide-react';
import { Footer } from './Footer';
import { useEmbedMode } from '../hooks/useEmbedMode';

const ADMIN_GROUP = 'AAM Developers';

export function Layout() {
    const { user, logout } = useAuth();
    const location = useLocation();
    const { isEmbedded } = useEmbedMode();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const isAdmin = useMemo(() => {
        return user?.groups?.includes(ADMIN_GROUP) ?? false;
    }, [user?.groups]);

    const navLinks = useMemo(() => {
        const links = [
            { path: '/dashboard', label: 'Meeting Polls', icon: LayoutDashboard, activePaths: ['/dashboard', '/events'] },
            { path: '/scheduling-links', label: 'Links', icon: LinkIcon, activePaths: ['/scheduling-links'] },
            { path: '/proposals', label: 'Proposals', icon: Inbox, activePaths: ['/proposals'] },
            { path: '/integrations', label: 'Integrations', icon: Settings, activePaths: ['/integrations'] },
        ];

        if (isAdmin) {
            links.push({ path: '/admin', label: 'Admin', icon: BarChart3, activePaths: ['/admin'] });
        }

        return links;
    }, [isAdmin]);

    const isLinkActive = (activePaths: string[]) => activePaths.some(p => location.pathname.startsWith(p));

    const closeMobileMenu = () => setMobileMenuOpen(false);

    return (
        <div className="admin-app min-h-screen">
            <header className="header">
                <div className="container header-content">
                    <Link to="/dashboard" className="header-logo">
                        <img src="/logo.png" alt="AAM Let's Meet" className="navbar-logo" />
                    </Link>

                    <nav className="header-nav desktop-nav">
                        {navLinks.map(link => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`header-nav-link${isLinkActive(link.activePaths) ? ' header-nav-link--active' : ''}`}
                            >
                                <link.icon size={16} />
                                {link.label}
                            </Link>
                        ))}
                    </nav>

                    <div className="header-user desktop-nav">
                        <span className="header-user-name">
                            {user?.name || user?.email}
                        </span>
                        <button
                            onClick={logout}
                            className="btn btn-ghost header-logout-btn"
                            title="Sign out"
                        >
                            <LogOut size={18} />
                        </button>
                    </div>

                    <button
                        className="mobile-menu-btn"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-label="Toggle menu"
                        aria-expanded={mobileMenuOpen}
                    >
                        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>

                {mobileMenuOpen && (
                    <div
                        className="mobile-nav-overlay"
                        onClick={closeMobileMenu}
                        aria-hidden="true"
                    />
                )}

                <nav
                    className={`mobile-nav${mobileMenuOpen ? ' mobile-nav--open' : ''}`}
                    aria-hidden={!mobileMenuOpen}
                >
                    <div className="mobile-nav-header">
                        <span className="mobile-nav-title">Menu</span>
                        <button
                            type="button"
                            onClick={closeMobileMenu}
                            className="mobile-nav-close"
                            aria-label="Close menu"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="mobile-nav-links">
                        {navLinks.map(link => (
                            <Link
                                key={link.path}
                                to={link.path}
                                onClick={closeMobileMenu}
                                className={`mobile-nav-link${isLinkActive(link.activePaths) ? ' mobile-nav-link--active' : ''}`}
                            >
                                <link.icon size={20} />
                                {link.label}
                            </Link>
                        ))}
                    </div>

                    <div className="mobile-nav-footer">
                        <div className="mobile-nav-user">
                            {user?.name || user?.email}
                        </div>
                        <button
                            type="button"
                            onClick={() => { closeMobileMenu(); logout(); }}
                            className="mobile-nav-logout"
                        >
                            <LogOut size={20} />
                            Sign Out
                        </button>
                    </div>
                </nav>
            </header>

            <main className="admin-app-main">
                <Outlet />
            </main>

            {!isEmbedded && <Footer />}
        </div>
    );
}
